import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useSettings, type AccountSettings } from "@/lib/account/settings";
import { useStoreSettings } from "@/lib/admin/settings";
import { AccountShell, SectionCard, Alert } from "@/components/account/AccountShell";

export const Route = createFileRoute("/account/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const [settings, save] = useSettings();
  const supportEmail = useStoreSettings().contact.email;
  const [saved, setSaved] = useState(false);

  function update<K extends keyof AccountSettings>(key: K, value: AccountSettings[K]) {
    save({ ...settings, [key]: value });
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  }

  return (
    <AccountShell>
      <SectionCard title="Preferences">
        {saved && (
          <div className="mb-6">
            <Alert tone="success">Preferences updated.</Alert>
          </div>
        )}
        <div className="divide-y divide-black/10">
          <ToggleRow
            title="Marketing emails"
            description="Curated stories, launches, and invitations from the Maison."
            checked={settings.marketingEmails}
            onChange={(v) => update("marketingEmails", v)}
          />
          <ToggleRow
            title="Order updates"
            description="Order confirmations, shipping notifications, and delivery updates."
            checked={settings.orderUpdates}
            onChange={(v) => update("orderUpdates", v)}
          />
          <ToggleRow
            title="New arrivals"
            description="Be the first to discover new fragrances and limited editions."
            checked={settings.newArrivals}
            onChange={(v) => update("newArrivals", v)}
          />
          <div className="grid gap-3 py-5 sm:grid-cols-[1fr_auto] sm:items-center">
            <div>
              <p className="text-sm font-medium text-black">Language</p>
              <p className="text-xs text-black/60">Choose how the site is displayed.</p>
            </div>
            <select
              value={settings.language}
              onChange={(e) => update("language", e.target.value as AccountSettings["language"])}
              className="border border-black/15 bg-white px-3 py-2 text-sm text-black focus:border-[#C6A15B] focus:outline-none"
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Danger Zone">
        <p className="text-sm text-black/70">
          To close your account or request a copy of your data, please contact{" "}
          <a className="text-[#C6A15B] hover:underline" href={`mailto:${supportEmail}`}>
            {supportEmail}
          </a>
          .
        </p>
      </SectionCard>
    </AccountShell>
  );
}

function ToggleRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-6 py-5">
      <div>
        <p className="text-sm font-medium text-black">{title}</p>
        <p className="mt-0.5 text-xs text-black/60">{description}</p>
      </div>
      <span
        className={
          "relative mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition " +
          (checked ? "bg-[#C6A15B]" : "bg-black/15")
        }
      >
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span
          className={
            "inline-block h-5 w-5 transform rounded-full bg-white shadow transition " +
            (checked ? "translate-x-5" : "translate-x-0.5")
          }
        />
      </span>
    </label>
  );
}
